// OPS Occult Archive visual-source refinement — Splendor Solis peacock / alchemical colour stage.
(()=>{
  const pack=window.OPS_OCCULT_EDITORIAL;
  if(!pack||!Array.isArray(pack.entries)) return;

  const duplicate=pack.entries.some((x)=>{
    const hay=[x&&x.title,x&&x.slug,x&&x.synopsis].filter(Boolean).join(' ').toLowerCase();
    return (hay.includes('splendor solis')&&hay.includes('peacock'))||hay.includes('v0025638')||hay.includes('cauda pavonis');
  });
  if(duplicate) return;

  pack.entries.push({
    title:'Splendor Solis — Peacock in the Alchemical Flask',
    slug:'splendor-solis-peacock-alchemical-flask',
    type:'Alchemy, colour symbolism & emblematic vessel',
    depth:'Deep Water',
    synopsis:'A peacock contained inside a crowned alchemical flask turns a stage of colour transformation into a memorable vertical emblem of vessel, bird and iridescent change.',
    reference:{
      period:'Watercolour 1900–1909, after the 1582 Splendor Solis manuscript tradition',
      place:'London; after British Library Harley MS 3469',
      tradition:'Alchemy, emblematic manuscripts & colour-stage imagery',
      status:'Rights-verified historical museum copy; selected digital image is CC BY 4.0'
    },
    illustration:{
      url:'https://commons.wikimedia.org/wiki/Special:Redirect/file/A%20peacock%20in%20a%20crowned%20alchemical%20flask%3B%20representing%20the%20st%20Wellcome%20V0025638.jpg?width=1400',
      alt:'Tall watercolour after Splendor Solis showing a peacock with a spread, eye-spotted tail enclosed inside a crowned glass alchemical flask, representing a stage in which the substance breaks into many colours.'
    },
    lead:[
      'This image is a particularly useful source for artists because the alchemical idea is carried by one immediately legible construction: a living bird contained inside laboratory glass. The narrow crowned flask creates a strong outer silhouette, while the peacock tail supplies an internal field of repeated eyes, arcs and shifting colour. It works simultaneously as creature study, vessel study, colour system and emblem.',
      'The selected image is not being presented as an untouched sixteenth-century manuscript scan. Wellcome Collection identifies it as a 1900–1909 watercolour by Edith Annie Ibbs, painted around 1907 on commission for the Historical Medical Exhibition and later shown in the Wellcome Historical Medical Museum. Wellcome records it as after Salomon Trismosin, Splendor solis, 1582, British Library Harley MS 3469. Wikimedia Commons identifies the reproduction as Wellcome photo V0025638 and licenses it under Creative Commons Attribution 4.0 International.'
    ],
    sections:[
      {
        heading:'Artist research: vessel, creature and colour stage',
        paragraphs:[
          'For illustration, printmaking, poster work, album art and game-world documents, separate the image into three decisions: the flask as a frame, the peacock as the active transformation, and the crown as a small sign of hierarchy at the neck. Rebuild those relationships around a different creature, material or fictional process rather than lifting the complete historic composition as a finished design.',
          'The most useful colour lesson is not to sample every historic hue. Study how many-coloured feather eyes are concentrated inside a simple transparent boundary. A new composition can use one restrained outer silhouette and a richer internal transformation, giving complex colour somewhere to live without making the whole page visually noisy.',
          'For tattoo reference, simplify hard. Keep the flask outline, bird posture and only a handful of enlarged tail-eye motifs; reduce tiny feather barbs, crown teeth and hairline glass details. Leave clear negative space around the peacock body and neck. For colour tattoos, use a smaller number of clearly separated colour masses so the tail remains readable as it heals and ages.'
        ]
      },
      {
        heading:'Follow the peacock-tail trail',
        paragraphs:[
          'In alchemical imagery the peacock is tied to a particular colour-stage tradition often discussed as the peacock’s tail or cauda pavonis. Treat that as a historical alchemical context, not as a universal meaning of peacocks. Before carrying a specific colour sequence or emblem into new work, follow it back through Splendor Solis and related alchemical texts and images.',
          'The wider Splendor Solis image family is especially useful for artists because related plates place other figures and creatures inside crowned vessels. A productive research route is Splendor Solis → peacock’s tail / cauda pavonis → colour stages → alchemical vessels → Mercury and sulphur personifications → emblem books. Compare the recurring vessel structure, then invent a new symbolic system instead of copying any one plate.'
        ]
      },
      {
        heading:'Why the copy history matters',
        paragraphs:[
          'This Wellcome watercolour is itself evidence of how historical images are recopied, catalogued and reinterpreted. Ibbs made it centuries after the manuscript it references. For artists and researchers that distinction is useful: compare the museum copy with the 1582 British Library manuscript tradition to see what survives, what is simplified and what may shift when an image passes through another hand and medium.',
          'When citing or adapting this specific digital image, credit Edith Annie Ibbs and Wellcome and retain the CC BY 4.0 licence information. When discussing the underlying sixteenth-century composition, distinguish that older source tradition from the twentieth-century watercolour rather than collapsing them into one date or creator.'
        ]
      }
    ],
    evidence:{
      label:'Rights-verified historical image',
      note:'Wikimedia Commons identifies Wellcome photo V0025638 and explicitly licenses the digital image under Creative Commons Attribution 4.0 International (CC BY 4.0). Reuse, including commercial adaptation, is permitted with appropriate credit, a licence link and indication of changes.'
    },
    visualResearch:{
      creator:'Edith Annie Ibbs (1863–1937), after Salomon Trismosin / Splendor Solis',
      date:'1900–1909; commissioned circa 1907',
      work:'A peacock in a crowned alchemical flask; Splendor solis series',
      underlyingSource:'After Salomon Trismosin, Splendor solis, 1582, British Library Harley MS 3469',
      institution:'Wellcome Collection, London',
      imageReference:'V0025638',
      collectionReference:'38825i',
      provenance:'Wellcome states that Edith Annie Ibbs painted the watercolour on commission from C. J. S. Thompson, secretary of the Historical Medical Exhibition organised by Henry S. Wellcome, circa 1907; it was subsequently exhibited in the Wellcome Historical Medical Museum, Wigmore Street, London.',
      rights:'Creative Commons Attribution 4.0 International (CC BY 4.0)',
      rightsUrl:'https://creativecommons.org/licenses/by/4.0/',
      attribution:'Edith Annie Ibbs, after Salomon Trismosin, Splendor solis; Wellcome Collection, London. Wellcome photo V0025638 / collection reference 38825i. CC BY 4.0.',
      verifiedOn:'2026-09-06'
    },
    sources:[
      {
        label:'Wikimedia Commons — Peacock in a crowned alchemical flask, Wellcome V0025638 (CC BY 4.0)',
        url:'https://commons.wikimedia.org/wiki/File:A_peacock_in_a_crowned_alchemical_flask;_representing_the_st_Wellcome_V0025638.jpg',
        type:'Rights-verified image source'
      },
      {
        label:'Wellcome Collection — Peacock in a crowned alchemical flask, by E. A. Ibbs',
        url:'https://wellcomecollection.org/works/pyhb4hmk',
        type:'Institutional object record, creator, provenance & source history'
      },
      {
        label:'Creative Commons — Attribution 4.0 International',
        url:'https://creativecommons.org/licenses/by/4.0/',
        type:'Reuse licence'
      }
    ],
    related:['Alchemy','Alchemical Apparatus: Furnace & Receiver','Sophia Mystica — Rosicrucian Body Map','Emblem books','Peacock’s tail / cauda pavonis','Splendor Solis'],
    catalogue:[]
  });
})();

// OPS Occult Archive visual-source refinement — chiromancy / historical hand diagrams.
(()=>{
  const pack=window.OPS_OCCULT_EDITORIAL;
  if(!pack||!Array.isArray(pack.entries)) return;

  const duplicate=pack.entries.some((x)=>{
    const hay=[x&&x.title,x&&x.slug,x&&x.synopsis].filter(Boolean).join(' ').toLowerCase();
    return hay.includes('l0068571')||(hay.includes('chiromancy')&&hay.includes('1648'))||hay.includes('three-hands-1648');
  });
  if(duplicate) return;

  pack.entries.push({
    title:'Chiromancy — Three Hands from a 1648 Palmistry Manual',
    slug:'chiromancy-three-hands-1648-palmistry-manual',
    type:'Chiromancy, hand diagrams & symbolic anatomy',
    depth:'Deep Water',
    synopsis:'Three drawn hands turn fingers, palms and creases into a compact symbolic map, offering a historical source for hand silhouettes, diagram systems and occult-era visual notation.',
    reference:{
      period:'1648',
      place:'England',
      tradition:'Chiromancy, palmistry, physiognomy & divinatory hand-reading',
      status:'Rights-verified historical visual source; selected Wellcome reproduction is CC BY 4.0'
    },
    illustration:{
      url:'https://commons.wikimedia.org/wiki/Special:Redirect/file/Illustration%20of%203%20hands%20in%20Chyromancy%20Wellcome%20L0068571.jpg?width=1400',
      alt:'1648 drawing from an English practical chiromancy manual showing three open hands side by side, with palm lines and small diagrammatic marks used for historical palm-reading instruction.'
    },
    lead:[
      'This page is useful to artists because the hand is already being treated as a graphic map. Three repeated palm silhouettes let you compare finger spacing, palm shape, line networks and symbolic marks while keeping the overall composition simple enough to read at a glance.',
      'Wikimedia Commons identifies the image as Wellcome photo L0068571 from an English manual of practical chiromancy or palmistry dated 1648, formerly the property of Lord Redesdale. The manuscript is catalogued as Wellcome Archives and Manuscripts MS8727. Commons explicitly licenses this digital reproduction under Creative Commons Attribution 4.0 International.'
    ],
    sections:[
      {
        heading:'Artist research: build a hand-map, not borrowed flash',
        paragraphs:[
          'For tattoo flash, illustration and printmaking, start with the hand silhouette rather than copying every historical line. Choose perhaps three to seven enlarged landmarks, keep the fingers clearly separated and replace dense hairline divisions with deliberate open areas. A hand with a few strong correspondences will remain readable at tattoo scale far better than a miniature facsimile of the manuscript page.',
          'For graphic design, album sleeves and game-world documents, the three-hand arrangement is as useful as the individual palm. Repeated hands can become comparison panels, stages, legends or alternate symbolic systems. Rebuild the marks around an original concept or researched historical convention instead of turning this page into a ready-made mystical-hand graphic.',
          'Chiromancy is a historical divinatory system, not a scientific map of personality or destiny. If specific palm lines, planetary signs or labels are retained, identify what the source tradition claimed they meant rather than presenting them as universal facts.'
        ]
      },
      {
        heading:'Follow the hand-diagram trail',
        paragraphs:[
          'Compare this 1648 manual with later palmistry diagrams, physiognomy manuals and astrological hand maps, then compare the underlying design idea with Zodiac Man and Wound Man elsewhere in the archive. All of them turn part or all of the body into a navigable field of labels and correspondences, but they do so for different historical purposes.',
          'A useful source trail continues into Wellcome’s 1698 The true fortune-teller, which combines chiromancy, physiognomy, metoposcopy and astrology and is marked Public Domain. Following several dated sources makes it easier to see which hand symbols recur, which change, and which belong to a particular author or period instead of treating modern palmistry diagrams as timeless.'
        ]
      }
    ],
    evidence:{
      label:'Rights-verified historical image',
      note:'Wikimedia Commons identifies Wellcome image L0068571 and explicitly licenses the digital reproduction under Creative Commons Attribution 4.0 International (CC BY 4.0). Commercial reuse and adaptation are permitted with appropriate attribution, a licence link and indication of changes when applicable.'
    },
    visualResearch:{
      creator:'Unknown manuscript artist / compiler',
      date:'1648',
      work:'English manual of practical chiromancy or palmistry',
      institution:'Wellcome Collection, London',
      imageReference:'L0068571',
      manuscriptReference:'Archives and Manuscripts MS8727',
      provenance:'English manual dated 1648, formerly the property of Lord Redesdale; the Commons/Wellcome record catalogues it as Archives and Manuscripts MS8727.',
      rights:'Creative Commons Attribution 4.0 International (CC BY 4.0)',
      rightsUrl:'https://creativecommons.org/licenses/by/4.0/',
      attribution:'Wellcome Library, London; Illustration of 3 hands in Chyromancy; MS8727; image L0068571. CC BY 4.0.',
      verifiedOn:'2026-09-06'
    },
    sources:[
      {
        label:'Wikimedia Commons — Illustration of 3 hands in Chyromancy, Wellcome L0068571 (CC BY 4.0)',
        url:'https://commons.wikimedia.org/wiki/File:Illustration_of_3_hands_in_Chyromancy_Wellcome_L0068571.jpg',
        type:'Rights-verified image source'
      },
      {
        label:'Wellcome Collection — English practical chiromancy / palmistry manual, MS8727',
        url:'https://wellcomecollection.org/works/n3a7848p',
        type:'Institutional manuscript record & source history'
      },
      {
        label:'Wellcome Collection — The true fortune-teller or guide to knowledge (1698)',
        url:'https://wellcomecollection.org/works/kpeefxzf',
        type:'Public-domain related source trail'
      },
      {
        label:'Creative Commons — Attribution 4.0 International',
        url:'https://creativecommons.org/licenses/by/4.0/',
        type:'Reuse licence'
      }
    ],
    related:['Divination','Zodiac Man (Homo Signorum)','Wound Man','Astrology','Physiognomy','Historical divination diagrams'],
    catalogue:[]
  });
})();

// OPS Occult Archive visual-source refinement — Great Comet of 1577 / celestial broadsides.
(()=>{
  const pack=window.OPS_OCCULT_EDITORIAL;
  if(!pack||!Array.isArray(pack.entries)) return;

  const duplicate=pack.entries.some((x)=>{
    const hay=[x&&x.title,x&&x.slug,x&&x.synopsis].filter(Boolean).join(' ').toLowerCase();
    return hay.includes('great comet of 1577')||hay.includes('great-comet-1577')||(hay.includes('comet')&&hay.includes('daschitz'));
  });
  if(duplicate) return;

  pack.entries.push({
    title:'Great Comet of 1577 — Celestial Broadside & Omen Image',
    slug:'great-comet-1577-celestial-broadside',
    type:'Celestial imagery, astronomical broadsides & omen culture',
    depth:'Deep Water',
    synopsis:'A dramatic 1577 woodcut turns a comet into a huge directional form across the night sky, combining observation, portent imagery, landscape and early-modern print design.',
    reference:{
      period:'1577',
      place:'Prague / Central Europe; surviving copy held in Zürich',
      tradition:'Early-modern comet observation, celestial broadsides & portent literature',
      status:'Rights-verified historical visual source; Public Domain Mark 1.0'
    },
    illustration:{
      url:'https://commons.wikimedia.org/wiki/Special:Redirect/file/Great_Comet_of_1577.gif',
      alt:'1577 woodcut of the Great Comet stretching as a broad radiant tail across a dark sky above a town and landscape, with printed text framing the celestial scene.'
    },
    lead:[
      'This woodcut is a strong artist source because one simple celestial event controls the whole page. A compact comet head expands into a huge directional tail, pulling the eye diagonally across a dark sky while the town and horizon provide scale. The result is useful for studying rays, movement, sky-ground contrast, old lettering and the way a single shape can dominate an otherwise detailed scene.',
      'Wikimedia Commons identifies the work as a 1577 woodcut by Jiri Daschitzsky (Jiří Jakubův Dačický), associated with a text by Peter Codicillus of Tulechova describing the comet seen after St Martin’s Day. The file record names Zentralbibliothek Zürich as the holding collection and marks the work Public Domain, including Public Domain Mark 1.0.'
    ],
    sections:[
      {
        heading:'Artist research: build motion from one celestial shape',
        paragraphs:[
          'For illustration, posters, record sleeves and game-world documents, study the hierarchy rather than tracing the page. The useful recipe is a small bright origin, an expanding directional tail, a low dark horizon and a field of secondary marks. Change the celestial body, landscape, angle and internal ray pattern to build an original composition with the same sense of scale and interruption.',
          'For tattoo reference, simplify the comet aggressively. Keep one clear head and perhaps two to five broad tail divisions; remove tiny stars, dense sky hatching, miniature buildings and historic text unless the lettering is deliberately redrawn at a readable scale. Give the rays enough separation that they do not close up as the tattoo ages.',
          'The old print texture is useful too: uneven black fields, blunt woodcut edges and simplified architecture create a very different visual language from modern smooth vector stars. Printmakers and illustrators can study those constraints, then recreate the roughness through their own marks rather than copying the exact block.'
        ]
      },
      {
        heading:'Observation, portent and historical context',
        paragraphs:[
          'Comets in sixteenth-century Europe could be observed as astronomical phenomena while also being interpreted through religious, political and omen traditions. Treat this particular broadside as evidence of that historical overlap, not as proof that a comet has one universal occult meaning.',
          'The Great Comet of 1577 also sits at an important point in astronomical history: Tycho Brahe’s observations helped argue that the comet lay beyond the Moon, challenging older ideas about an unchanging celestial realm. That makes the image useful for a research library because it connects visual omen culture with changing methods of astronomical observation.'
        ]
      },
      {
        heading:'Follow the celestial-print trail',
        paragraphs:[
          'A useful visual route is Great Comet of 1577 → comet broadsides → celestial omens → astronomical pamphlets → Tycho Brahe → star atlases → constellation engravings → planetary diagrams. Compare how the same sky can be pictured as a dramatic event, a measured diagram or a mapped field of stars.',
          'For album art and worldbuilding in particular, this trail offers a way to invent believable historical-looking celestial documents without borrowing from contemporary fantasy artists: combine period printing methods, researched astronomical structures and original fictional events.'
        ]
      }
    ],
    evidence:{
      label:'Rights-verified historical image',
      note:'Wikimedia Commons identifies the 1577 woodcut as public domain in its country of origin and the United States and applies Creative Commons Public Domain Mark 1.0, indicating that the file is free of known copyright restrictions.'
    },
    visualResearch:{
      creator:'Jiri Daschitzsky (Jiří Jakubův Dačický / Georgium Jacobum von Datschitz)',
      textAuthor:'Peter Codicillus of Tulechova',
      date:'1577',
      work:'Woodcut broadside concerning the Great Comet of 1577',
      institution:'Zentralbibliothek Zürich, Zürich, Switzerland',
      medium:'Woodcut',
      dimensions:'26.3 × 35.5 cm',
      provenance:'Commons records the surviving image with Zentralbibliothek Zürich and identifies it as a 1577 woodcut connected with a printed account of the comet seen on 12 November 1577.',
      rights:'Public domain; Creative Commons Public Domain Mark 1.0',
      rightsUrl:'https://creativecommons.org/publicdomain/mark/1.0/',
      attribution:'Jiri Daschitzsky, Great Comet of 1577, woodcut, 1577. Zentralbibliothek Zürich. Public Domain Mark 1.0.',
      verifiedOn:'2026-09-07'
    },
    sources:[
      {
        label:'Wikimedia Commons — Great Comet of 1577 woodcut (Public Domain Mark 1.0)',
        url:'https://commons.wikimedia.org/wiki/File:Great_Comet_of_1577.gif',
        type:'Rights-verified image source, creator, date, medium & collection'
      },
      {
        label:'Creative Commons — Public Domain Mark 1.0',
        url:'https://creativecommons.org/publicdomain/mark/1.0/',
        type:'Rights status reference'
      }
    ],
    related:['Astronomy','Astrology','Celestial omens','Comets','Tycho Brahe','Star atlases','Planetary diagrams','Early-modern print culture'],
    catalogue:[]
  });
})();

// OPS Occult Archive visual-source refinement — ritual geometry / Francis Barrett magic circle.
(()=>{
  const pack=window.OPS_OCCULT_EDITORIAL;
  if(!pack||!Array.isArray(pack.entries)) return;

  const duplicate=pack.entries.some((x)=>{
    const hay=[x&&x.title,x&&x.slug,x&&x.synopsis].filter(Boolean).join(' ').toLowerCase();
    return hay.includes('barrett-magic-circle-1801')||(hay.includes('francis barrett')&&hay.includes('magic circle'))||(hay.includes('the magus')&&hay.includes('ritual geometry'));
  });
  if(duplicate) return;

  pack.entries.push({
    title:'Francis Barrett’s Magic Circle — Ritual Geometry from The Magus',
    slug:'barrett-magic-circle-1801-ritual-geometry',
    type:'Ceremonial magic, ritual geometry & diagram systems',
    depth:'Deep Water',
    synopsis:'An 1801 magic-circle engraving turns concentric boundaries, names, crosses and directional divisions into a dense but highly structured visual system for studying ritual geometry and historical occult diagram design.',
    reference:{
      period:'1801',
      place:'London, England',
      tradition:'Ceremonial magic, talismanic practice & early-modern/modern occult compilation',
      status:'Rights-verified historical source; Public Domain Mark 1.0'
    },
    illustration:{
      url:'https://commons.wikimedia.org/wiki/Special:Redirect/file/The_magic_circle_barrett_the_magus.jpg?width=1400',
      alt:'Black-and-white 1801 magic-circle diagram from Francis Barrett’s The Magus, built from concentric circular bands, crosses, names, letters and directional divisions arranged around an open central field.'
    },
    lead:[
      'This diagram fills a useful gap in the archive because it is not primarily a figure, creature, vessel or celestial scene: the geometry itself is the subject. Concentric boundaries, repeated names, crosses, radial divisions and changes in lettering create a system that can be studied as information design as much as occult imagery.',
      'Wikimedia Commons identifies the plate as the magic-circle illustration from Francis Barrett’s The Magus (1801), and applies Public Domain Mark 1.0; Barrett died in 1830. Wellcome Collection independently holds and digitises both Barrett’s 1801 author manuscript and the printed 1801 edition, and marks those works Public Domain.'
    ],
    sections:[
      {
        heading:'Artist research: study the system before the decoration',
        paragraphs:[
          'For graphic design, printmaking, album art and game-world documents, break the circle into layers: outer boundary, textual ring, repeated marks, radial or directional structure, and a quieter centre. Rebuild those layers from your own researched symbols, invented nomenclature or worldbuilding rules rather than copying Barrett’s full arrangement as a finished occult graphic.',
          'The useful lesson is hierarchy. Dense lettering is held inside strong circles, while small crosses and marks repeat as anchors. An original diagram can preserve that rhythm without reusing the exact names or sacred formulae. This is especially valuable for fictional maps, ritual interfaces, record sleeves and prop documents where the viewer should feel that the image has an internal logic.',
          'For tattoo reference, simplify aggressively. Keep two or three major rings, enlarge only a small number of glyphs or words, remove hairline rules and tiny inscriptions, and leave a generous open centre. At small scale, a literal facsimile would collapse into noise; the better source lesson is how the rings organise information.'
        ]
      },
      {
        heading:'Context: a particular ceremonial-magic source, not a universal circle',
        paragraphs:[
          'Treat this plate as one historical diagram associated with Barrett’s 1801 occult compilation, not as the universal or definitive form of a magic circle. Barrett drew on older magical, cabalistic, astrological and ceremonial sources, so the productive research question is what he retained, rearranged or republished rather than assuming every circle tradition uses the same names and structure.',
          'When specific divine names, angelic names or sacred letters matter to a new work, follow them through the source trail and understand their religious and linguistic context before reusing them decoratively. For many contemporary designs, replacing the text layer with original material is both more respectful and more creatively useful.'
        ]
      },
      {
        heading:'Follow the ritual-diagram trail',
        paragraphs:[
          'A strong visual path is Barrett’s The Magus → ceremonial magic circles → talismans → planetary and angelic names → magic squares and kameas → grimoire diagrams → directional ritual layouts. Compare what each diagram is trying to organise: space, timing, planetary correspondence, protection, invocation, or a sequence of actions.',
          'For artists, the goal is to build a vocabulary of systems rather than collect finished designs. Compare several historical circles side by side, note which structures recur and which are source-specific, then construct a new diagram from researched components or original worldbuilding logic.'
        ]
      }
    ],
    evidence:{
      label:'Rights-verified historical image',
      note:'Wikimedia Commons identifies the 1801 Francis Barrett magic-circle image as a faithful reproduction of a public-domain work and applies Creative Commons Public Domain Mark 1.0. Wellcome Collection marks its digitised 1801 The Magus materials Public Domain and states they may be used for any purpose without copyright restriction.'
    },
    visualResearch:{
      creator:'Francis Barrett (1774–1830)',
      date:'1801',
      work:'The Magus, or Celestial Intelligencer — magic circle illustration',
      publication:'London: Printed for Lackington, Allen and Co., 1801',
      institution:'Wellcome Collection (comparative digitised 1801 manuscript and printed edition); image file via Wikimedia Commons',
      manuscriptReference:'Wellcome MS.1072',
      printedEditionReference:'Wellcome EPB/C/12317',
      provenance:'Commons identifies the image as an illustration from Barrett’s The Magus (1801). Wellcome’s author holograph MS.1072 and printed 1801 edition provide an institutional source trail for the work and its talismanic, magical and astrological contents.',
      rights:'Public domain; Creative Commons Public Domain Mark 1.0',
      rightsUrl:'https://creativecommons.org/publicdomain/mark/1.0/',
      attribution:'Francis Barrett, magic circle from The Magus, or Celestial Intelligencer, 1801. Public Domain Mark 1.0. Institutional source trail: Wellcome Collection.',
      verifiedOn:'2026-09-07'
    },
    sources:[
      {
        label:'Wikimedia Commons — The magic circle, Barrett’s The Magus (1801)',
        url:'https://commons.wikimedia.org/wiki/File:The_magic_circle_barrett_the_magus.jpg',
        type:'Rights-verified image source; Public Domain Mark 1.0'
      },
      {
        label:'Wellcome Collection — The Magus or Celestial Intelligencer, author holograph MS.1072',
        url:'https://wellcomecollection.org/works/xetw9sy7',
        type:'Public-domain institutional manuscript record & provenance trail'
      },
      {
        label:'Wellcome Collection — The Magus, printed edition (London, 1801)',
        url:'https://wellcomecollection.org/works/m54n2gqn',
        type:'Public-domain institutional printed source'
      },
      {
        label:'Creative Commons — Public Domain Mark 1.0',
        url:'https://creativecommons.org/publicdomain/mark/1.0/',
        type:'Rights status reference'
      }
    ],
    related:['Ceremonial magic','Magic circles','Talismans','Magic squares','Kameas','Planetary magic','Grimoires','Ritual diagrams'],
    catalogue:[]
  });
})();
